/**
 * Order Sources API Queries
 *
 * Query keys and read operations. All reads go through /api/order_sources
 * (the canonical paginated endpoint); the simple `/order_sources.json`
 * dropdown endpoint was retired in favour of one filtered API call.
 */

import { apiClient } from '../client';

import type {
  OrderSourcesListResponse,
  OrderSourceShowResponse,
  FetchOrderSourcesListParams,
} from './types';

export const orderSourcesQueryKeys = {
  all: ['orderSources'] as const,
  lists: () => [...orderSourcesQueryKeys.all, 'list'] as const,
  list: (params: FetchOrderSourcesListParams) =>
    [...orderSourcesQueryKeys.lists(), params] as const,
  details: () => [...orderSourcesQueryKeys.all, 'detail'] as const,
  detail: (id: number) => [...orderSourcesQueryKeys.details(), id] as const,
};

// List consumer — used by both the management page (paginated) and the
// order-creation dropdown (status=active filter). Same endpoint, different params.
// Truncation (total > returned) is normal here for the paginated management page,
// so it's NOT flagged at this layer — the dropdown call site (CreateOrderContext)
// does its own non-paginated-truncation check.
export async function getOrderSources(
  params: FetchOrderSourcesListParams = {},
): Promise<OrderSourcesListResponse> {
  const { data } = await apiClient.get<OrderSourcesListResponse>('/api/order_sources', { params });
  return data;
}

// Single-record consumer — used by the details page (initialData from Phlex
// first-paint, then React Query keeps the data fresh after edits).
export async function getOrderSource(id: number): Promise<OrderSourceShowResponse> {
  const { data } = await apiClient.get<OrderSourceShowResponse>(`/api/order_sources/${id}`);
  return data;
}
